import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, UserCheck, DollarSign, TrendingUp } from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color, testId }) => (
  <Card className="bg-[#141414] border-[#27272A] hover:border-[#3f3f46] transition-all duration-200" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500">{title}</p>
          <p className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight text-white mt-1">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [gestores, setGestores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/dashboard'),
      api.get('/admin/gestores')
    ]).then(([statsRes, gestoresRes]) => {
      setStats(statsRes.data);
      setGestores(gestoresRes.data.slice(0, 5));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 bg-[#141414]" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-dashboard">
      <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
        DASHBOARD ADMINISTRATIVO
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Gestores" value={stats?.total_gestores || 0} icon={Users} color="bg-[#007AFF]/10 text-[#007AFF]" testId="admin-stats-gestores" />
        <StatCard title="Gestores Ativos" value={stats?.gestores_ativos || 0} icon={UserCheck} color="bg-green-500/10 text-green-400" testId="admin-stats-ativos" />
        <StatCard title="Total Alunos" value={stats?.total_alunos || 0} icon={Users} color="bg-purple-500/10 text-purple-400" testId="admin-stats-alunos" />
        <StatCard title="Faturamento" value={`R$ ${(stats?.faturamento || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} icon={DollarSign} color="bg-amber-500/10 text-amber-400" testId="admin-stats-faturamento" />
      </div>

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold font-['Barlow_Condensed'] tracking-tight uppercase text-white">
              GESTORES RECENTES
            </h2>
            <TrendingUp className="w-4 h-4 text-zinc-500" />
          </div>
          {gestores.length === 0 ? (
            <p className="text-zinc-500 text-sm">Nenhum gestor cadastrado ainda.</p>
          ) : (
            <div className="space-y-3">
              {gestores.map(g => (
                <div key={g.id} className="flex items-center justify-between py-2 border-b border-[#27272A] last:border-0">
                  <div>
                    <p className="text-sm font-medium text-white">{g.name}</p>
                    <p className="text-xs text-zinc-500">{g.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-md font-medium ${
                    g.status === 'ativo' ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'
                  }`}>
                    {g.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
